import axios, { Method } from 'axios';
import { immerable } from 'immer';
import moment, { Moment } from 'moment';

import { IMap } from 'helpers/types';
import { REMOTE_BASE_URL } from 'state_management/env_constants';

import { httpClient } from './http_client';

// ------------------------------------------------------------------------------------------------
// Defines possible RoleAliases
// ------------------------------------------------------------------------------------------------
export enum RoleAlias {
  NONE = '',
  ADMIN = 'Admin',
  CREATOR = 'Creator',
  VIEWER = 'Viewer'
}

// ------------------------------------------------------------------------------------------------
// Defines timestamps object within many responses
// ------------------------------------------------------------------------------------------------
export interface ITimestamps {
  created: string;
  updated: string;
  deleted?: string;
}

// ------------------------------------------------------------------------------------------------
// Defines an object containing only ID field. These are used in requests as arrays, and in some
// responses.
// ------------------------------------------------------------------------------------------------
export interface IID {
  id: string;
}

// ------------------------------------------------------------------------------------------------
// Wrapper function to call HttpClient.request with strict typing of request/response.
// ------------------------------------------------------------------------------------------------
export async function sendRequest<R, T = any>(method: Method, url: string, body?: T, headers?: IMap) {
  return httpClient
    .request<R>({
      url: url,
      method: method,
      data: body,
      headers: headers
    })
    .then((resp) => {
      return resp.data;
    });
}

// ------------------------------------------------------------------------------------------------
// Class that wraps up timestamps object present in many responses.
// ------------------------------------------------------------------------------------------------
export class Timestamps {
  [immerable] = true;
  readonly created: Moment;
  readonly updated: Moment;
  readonly deleted?: Moment;
  constructor(created: Moment, updated: Moment, deleted?: Moment) {
    this.created = created;
    this.updated = updated;
    this.deleted = deleted;
  }
  toJson(): ITimestamps {
    return {
      // No need to shift these dates
      created: this.created.isValid() ? this.created.toISOString() : '',
      updated: this.updated.isValid() ? this.updated.toISOString() : '',
      deleted: this.deleted?.isValid() ? this.deleted.toISOString() : ''
    };
  }
  static fromJson(json: ITimestamps): Timestamps {
    return new Timestamps(
      // No need to unshift these dates
      json.created ? moment(json.created) : moment.invalid(),
      json.updated ? moment(json.updated) : moment.invalid(),
      json.deleted ? moment(json.deleted) : moment.invalid()
    );
  }
}

export const NULL_TIMESTAMPS = new Timestamps(moment.invalid(), moment.invalid());

export interface EnvironmentStatus {
  services: { [key: string]: { Ready: boolean; Version: string } };
  status: string;
}
export const getEnvironmentStatus = () => {
  return sendRequest<EnvironmentStatus>('GET', `api/v1/status`);
};

export const checkSSOUser = async (email: string) => {
  try {
    const response = await axios.get<{ ssoEnabled: boolean }>(`${REMOTE_BASE_URL}/api/v1/user/sso-enabled?email=${email}`);
    return response?.data?.ssoEnabled;
  } catch (error) {
    logger.error('Failed to check SSO user', { error });
    return false;
  }
};
